import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, FlaskConical, Leaf, ChefHat, Check, HeartHandshake } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { ContactForm } from "../components/ContactForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

export default function Home() {
  const { lang, copy } = useLanguage();
  const [posts, setPosts] = useState([]);
  const { scrollY } = useScroll();
  const blobY = useTransform(scrollY, [0, 600], [0, 60]);

  useEffect(() => {
    axios.get(`${API}/posts?lang=${lang}`).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, [lang]);

  const pillarIcons = [FlaskConical, Leaf, ChefHat];

  return (
    <main>
      <Seo
        title={copy.seo.home.title}
        description={copy.seo.home.desc}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Gusto y Ciencia",
          description: copy.seo.home.desc,
          url: window.location.origin,
          image: `${window.location.origin}/giada-v2.jpg`,
          telephone: "+34613234203",
          email: "hola@gustoyciencia.com",
          priceRange: "20€ - 350€",
          address: { "@type": "PostalAddress", addressLocality: "Valencia", addressCountry: "ES" },
          areaServed: ["Valencia", "Online"],
          founder: { "@type": "Person", name: "Giada", jobTitle: "Coach de nutrición y bienestar" },
          sameAs: [],
        }}
      />
      {/* HERO — typographic, no photo */}
      <section className="relative overflow-hidden pt-32 pb-20 sm:pt-44 sm:pb-28" data-testid="hero-section">
        <motion.div style={{ y: blobY }} className="absolute -top-40 -right-40 h-[540px] w-[540px] rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />
        <motion.div style={{ y: blobY }} className="absolute top-72 -left-48 h-[460px] w-[460px] rounded-full bg-pine/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-5 text-center">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-6 text-xs font-mono uppercase tracking-[0.24em] text-clay font-semibold"
            data-testid="hero-eyebrow"
          >
            {copy.hero.eyebrow}
          </motion.p>
          <h1 className="font-serif font-semibold tracking-tight text-ink text-4xl sm:text-6xl lg:text-7xl leading-[1.05]" data-testid="hero-title">
            {[copy.hero.line1, copy.hero.line2, copy.hero.line3].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1.5">
                <motion.span className={`block ${i === 2 ? "italic text-clay" : ""}`} {...lineAnim(i)}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 max-w-2xl mx-auto text-base sm:text-lg text-muted leading-relaxed"
            data-testid="hero-subtitle"
          >
            {copy.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-9 flex flex-col sm:flex-row justify-center gap-3"
          >
            <Link
              to="/prenota"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-clay px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-clay-dark hover:shadow-xl hover:-translate-y-0.5"
            >
              {copy.hero.ctaPrimary} <ArrowRight size={16} />
            </Link>
            <a
              href="#metodo"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center rounded-full border border-ink/20 px-8 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:border-pine hover:text-pine"
            >
              {copy.hero.ctaSecondary}
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex flex-wrap justify-center gap-x-7 gap-y-2"
            data-testid="hero-badges"
          >
            {copy.hero.badges.map((b, i) => (
              <span key={i} className="flex items-center gap-1.5 text-xs font-medium text-sage">
                <Check size={14} className="text-gold" /> {b}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* PILLARS */}
      <section id="metodo" className="py-20 sm:py-28" data-testid="pillars-section">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">{copy.pillars.eyebrow}</p>
            <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-ink tracking-tight text-balance max-w-2xl">{copy.pillars.title}</h2>
            <p className="mt-4 text-muted max-w-xl">{copy.pillars.sub}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {copy.pillars.items.map((p, i) => {
              const Icon = pillarIcons[i];
              return (
                <Reveal key={i} delay={i * 0.12}>
                  <div
                    data-testid={`pillar-card-${i}`}
                    className="group h-full rounded-3xl bg-white border border-border p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:border-pine/30"
                  >
                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-pine-light text-pine transition-colors duration-500 group-hover:bg-clay group-hover:text-white">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-serif font-semibold text-xl text-ink mb-2.5">{p.title}</h3>
                    <p className="text-sm text-muted leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 sm:py-28 bg-sand/60" data-testid="services-preview-section">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">{copy.services.eyebrow}</p>
            <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-ink tracking-tight">{copy.services.title}</h2>
            <p className="mt-4 text-muted max-w-xl">{copy.services.sub}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {copy.services.items.slice(0, 3).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.12}>
                <div
                  data-testid={`service-card-${s.id}`}
                  className={`relative h-full rounded-3xl border p-7 flex flex-col transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl ${
                    s.id === "mes" ? "bg-pine text-linen border-pine" : "bg-white border-border hover:border-pine/30"
                  }`}
                >
                  {s.id === "mes" && (
                    <span className="absolute -top-3 left-6 rounded-full bg-clay px-3 py-1 text-[11px] font-mono font-semibold uppercase tracking-widest text-white">
                      {copy.services.popular}
                    </span>
                  )}
                  <p className={`text-xs font-mono uppercase tracking-widest ${s.id === "mes" ? "text-gold" : "text-clay"}`}>{s.tag}</p>
                  <h3 className={`mt-2 font-serif font-semibold text-2xl ${s.id === "mes" ? "text-linen" : "text-ink"}`}>{s.title}</h3>
                  <p className={`mt-1 text-sm ${s.id === "mes" ? "text-linen/70" : "text-muted"}`}>{s.duration}</p>
                  <p className={`mt-4 font-serif text-4xl font-semibold ${s.id === "mes" ? "text-gold" : "text-pine"}`}>{s.price}</p>
                  <p className={`mt-4 text-sm leading-relaxed flex-1 ${s.id === "mes" ? "text-linen/85" : "text-muted"}`}>{s.desc}</p>
                  <Link
                    to={`/contacto?servicio=${s.id}`}
                    data-testid={`service-cta-${s.id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors duration-300 ${
                      s.id === "mes" ? "bg-clay text-white hover:bg-clay-dark" : "border border-pine text-pine hover:bg-pine hover:text-linen"
                    }`}
                  >
                    {copy.services.cta} <ArrowRight size={15} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-8 text-center">
              <Link to="/servicios" data-testid="services-view-all" className="inline-flex items-center gap-2 text-sm font-semibold text-clay hover:text-clay-dark transition-colors">
                {copy.services.title} <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MICROBIOTA — soft light */}
      <section className="py-20 sm:py-28 bg-pine-light/60 overflow-hidden" data-testid="microbiota-section">
        <div className="max-w-6xl mx-auto px-5 grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">{copy.microbiota.eyebrow}</p>
            <h2 className="font-serif font-semibold text-3xl sm:text-4xl tracking-tight text-ink text-balance">{copy.microbiota.title}</h2>
            <p className="mt-5 text-muted leading-relaxed">{copy.microbiota.text}</p>
            <ol className="mt-8 space-y-4">
              {copy.microbiota.steps.map((step, i) => (
                <li key={i} className="flex items-center gap-4" data-testid={`microbiota-step-${i}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-pine/40 font-mono text-sm text-pine">{i + 1}</span>
                  <span className="text-sm sm:text-base text-ink/80">{step}</span>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-xs font-mono uppercase tracking-widest text-clay" data-testid="microbiota-claim">{copy.microbiota.claim}</p>
            <Link
              to="/contacto?servicio=microbiota"
              data-testid="microbiota-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-linen transition-all duration-300 hover:bg-pine-dark hover:-translate-y-0.5"
            >
              {copy.services.items[3].title} · 350€ <ArrowRight size={15} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-clay/10 -rotate-2" aria-hidden="true" />
              <img
                src="https://images.unsplash.com/photo-1766232578337-ff7bdb6d1f8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxoZXJiYWwlMjB0ZWElMjBoZWFsdGglMjBuYXR1cmFsfGVufDB8fHx8MTc5MDc2NDQwMXww&ixlib=rb-4.1.0&q=85"
                alt="Salud intestinal y microbiota"
                data-testid="microbiota-image"
                className="relative rounded-[2rem] object-cover w-full aspect-[4/3] shadow-xl"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-20 sm:py-28" data-testid="comparison-section">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">{copy.comparison.eyebrow}</p>
            <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-ink tracking-tight text-balance max-w-3xl">{copy.comparison.title}</h2>
            <p className="mt-4 text-muted max-w-xl">{copy.comparison.sub}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {copy.comparison.columns.map((col, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div
                  data-testid={`comparison-card-${i}`}
                  className={`h-full rounded-3xl p-7 ${
                    i === 0
                      ? "bg-pine-light border-2 border-pine/30 shadow-lg lg:-translate-y-3"
                      : "bg-white border border-border"
                  }`}
                >
                  {i === 0 && (
                    <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-gold-light border border-gold/40 px-3 py-1 text-[11px] font-mono font-semibold uppercase tracking-widest text-pine">
                      Giada
                    </span>
                  )}
                  <h3 className="font-serif font-semibold text-xl text-ink">{col.role}</h3>
                  <p className={`mt-1 text-xs font-mono uppercase tracking-widest ${i === 0 ? "text-pine" : "text-clay"}`}>{col.badge}</p>
                  <ul className="mt-5 space-y-3">
                    {col.items.map((item, j) => (
                      <li key={j} className="flex gap-2.5 text-sm leading-relaxed text-muted">
                        <Check size={15} className={`mt-0.5 shrink-0 ${i === 0 ? "text-pine" : "text-sage"}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10 rounded-3xl border border-gold/50 bg-gold-light/70 px-7 py-6" data-testid="comparison-note">
              <p className="text-sm sm:text-base text-ink/80 leading-relaxed font-medium">{copy.comparison.note}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SOLIDARITY */}
      <section className="py-16 sm:py-20" data-testid="solidarity-section">
        <div className="max-w-4xl mx-auto px-5">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-pine px-8 py-12 sm:px-14 sm:py-14 text-center">
              <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-gold/15 blur-2xl" aria-hidden="true" />
              <HeartHandshake size={36} className="mx-auto text-gold mb-5" />
              <p className="text-xs font-mono uppercase tracking-[0.22em] text-gold font-semibold mb-3">{copy.solidarity.eyebrow}</p>
              <h2 className="font-serif font-semibold text-2xl sm:text-3xl text-linen tracking-tight text-balance">{copy.solidarity.title}</h2>
              <p className="mt-4 text-linen/80 leading-relaxed max-w-xl mx-auto text-sm sm:text-base">{copy.solidarity.text}</p>
              <Link
                to="/contacto"
                data-testid="solidarity-cta"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-clay hover:text-white hover:-translate-y-0.5"
              >
                {copy.solidarity.cta} <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT — small natural photo, near the end */}
      <section className="py-20 sm:py-28" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-5 grid gap-12 lg:grid-cols-[0.65fr_1.35fr] lg:items-center">
          <Reveal>
            <div className="relative mx-auto max-w-[240px]">
              <div className="absolute -inset-2.5 rounded-full bg-clay/15 rotate-3" aria-hidden="true" />
              <img
                src="/giada-v2.jpg"
                alt="Giada, coach de nutrición y bienestar"
                data-testid="about-portrait"
                className="relative rounded-full object-cover object-[62%_center] w-full aspect-square shadow-lg border-4 border-white"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">{copy.about.eyebrow}</p>
            <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-ink tracking-tight">{copy.about.title}</h2>
            <p className="mt-5 text-muted leading-relaxed">{copy.about.p1}</p>
            <p className="mt-4 text-muted leading-relaxed">{copy.about.p3}</p>
            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {copy.about.credentials.map((cr, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-ink/80">
                  <Check size={15} className="text-clay shrink-0" /> {cr}
                </li>
              ))}
            </ul>
            <Link
              to="/sobre-mi"
              data-testid="about-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-pine px-6 py-3 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-pine hover:text-linen"
            >
              {copy.about.cta} <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="py-20 sm:py-28 bg-sand/60" data-testid="blog-preview-section">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">{copy.blog.eyebrow}</p>
            <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-ink tracking-tight">{copy.blog.title}</h2>
            <p className="mt-4 text-muted max-w-xl">{copy.blog.sub}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {posts.map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.12}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group block h-full rounded-3xl bg-white border border-border overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl"
                >
                  <div className="overflow-hidden">
                    <img src={post.image} alt={post.title} className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] font-mono uppercase tracking-widest text-clay">{post.category} · {post.read_time}</p>
                    <h3 className="mt-2 font-serif font-semibold text-lg text-ink leading-snug group-hover:text-pine transition-colors">{post.title}</h3>
                    <p className="mt-2 text-sm text-muted line-clamp-2">{post.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-20 sm:py-28" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-5">
          <Reveal>
            <div className="text-center mb-10">
              <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">{copy.contact.eyebrow}</p>
              <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-ink tracking-tight">{copy.contact.title}</h2>
              <p className="mt-4 text-muted">{copy.contact.sub}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-white border border-border p-6 sm:p-9 shadow-sm">
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
